/**
 * The vocabulary of the invitation → registration → first-login trace (XT-706):
 * one step name per thing that can happen, and one formatter shared by the
 * Slack message and the browser console mirror so the two can never drift.
 *
 * Lives in `shared` because both halves of the journey emit these: the API
 * (invite creation, Clerk webhook, first authenticated call) and the browser
 * (link opened, form submitted, Clerk answered).
 */

import { invitationTraceId } from './trace-id';

/** Steps the API emits. */
export const SERVER_TRACE_STEPS = [
  'invite.created',
  'invite.allowlist_failed',
  /** The invitation-email fallback failure that used to be swallowed. */
  'invite.email_failed',
  /**
   * Clerk had already emailed this invitee, so the fallback was unnecessary.
   * Informational, NOT a failure — the invitation did go out.
   */
  'invite.email_already_sent',
  'invite.skipped',
  'invite.copy_email_failed',
  'webhook.received',
  /** Every previously-silent early return out of the Clerk webhook. */
  'webhook.dropped',
  /** The `Invited -> Active` link — previously only an audit row. */
  'webhook.linked',
  /** A Clerk user with no invited row got an account anyway. */
  'webhook.created_account',
  'webhook.user_deleted',
  /**
   * A rejected authentication. There is deliberately no `login.ok`: a
   * SUCCESSFUL login is ordinary traffic for every returning user, and tracing
   * it filled the channel with lines that had nothing to do with an invitation.
   * `webhook.linked` already marks the journey's happy ending — the point where
   * an invited row actually becomes usable.
   */
  'login.rejected',
] as const;

/** Steps the browser beacons. */
export const CLIENT_TRACE_STEPS = [
  'link.opened',
  'signup.attempted',
  'signup.ok',
  /**
   * Clerk answered something other than `complete` — no error, no navigation.
   * The form just looks dead. Without this step the trace shows `attempted`
   * and then nothing, which is the exact hole that made XT-706 hard to find.
   */
  'signup.stalled',
  'signup.failed',
  /**
   * Only FAILING sign-ins are traced. A successful one is ordinary traffic; an
   * `attempted` step for it just doubled the noise for every returning user.
   */
  'signin.stalled',
  'signin.failed',
  /**
   * A completed sign-in. Traced so a run of `signin.failed` can be read to its
   * conclusion: without it, a user who mistypes a password twice and then gets
   * in is indistinguishable from one who is still locked out.
   */
  'signin.ok',
  /** The cross-domain transfer `.catch(() => {})` that logged nothing. */
  'signin.transfer_failed',
] as const;

export const INVITE_TRACE_STEPS = [
  ...SERVER_TRACE_STEPS,
  ...CLIENT_TRACE_STEPS,
] as const;

export type ServerTraceStep = (typeof SERVER_TRACE_STEPS)[number];
export type ClientTraceStep = (typeof CLIENT_TRACE_STEPS)[number];
export type InviteTraceStep = (typeof INVITE_TRACE_STEPS)[number];

const CLIENT_STEP_SET: ReadonlySet<string> = new Set(CLIENT_TRACE_STEPS);

export const isClientTraceStep = (value: unknown): value is ClientTraceStep =>
  typeof value === 'string' && CLIENT_STEP_SET.has(value);

const STEP_SET: ReadonlySet<string> = new Set(INVITE_TRACE_STEPS);

export const isInviteTraceStep = (value: unknown): value is InviteTraceStep =>
  typeof value === 'string' && STEP_SET.has(value);

/**
 * A step's extra detail. Deliberately flat scalars: every value has to survive
 * the client-events `meta` transport (scalars only, truncated) and render on
 * one Slack line.
 */
export type InviteTraceFields = Record<
  string,
  string | number | boolean | null | undefined
>;

export type InviteTraceEvent = {
  step: InviteTraceStep;
  /** Present whenever the email is known; absent on the ticket-only landing. */
  email?: string | null;
  /**
   * Overrides the email-derived id. Only for a step that knows the id but not
   * the address (a browser step replaying a server-computed id).
   */
  traceId?: string;
  fields?: InviteTraceFields;
};

/**
 * One emoji per step, so a journey is readable at a glance without parsing the
 * step names: 📨 something was sent, 🔗 the account is now usable, ❌ broken,
 * ⚠️ suspicious but not fatal.
 *
 * Chosen to make the SHAPE of a journey obvious when scrolling — a healthy one
 * runs 📨 → 👆 → ✍️ → ✅ → 🔗, and any ❌ or ⚠️ stands out against it.
 */
const STEP_ICONS: Record<InviteTraceStep, string> = {
  // Invite created and on its way.
  'invite.created': '📨',
  'invite.email_already_sent': '📨',
  'invite.allowlist_failed': '❌',
  'invite.email_failed': '❌',
  'invite.copy_email_failed': '❌',
  'invite.skipped': '⚠️',
  // The invitee's own steps through the browser.
  'link.opened': '👆',
  'signup.attempted': '✍️',
  'signup.ok': '✅',
  'signup.stalled': '⚠️',
  'signup.failed': '❌',
  'signin.stalled': '⚠️',
  'signin.failed': '❌',
  'signin.ok': '✅',
  'signin.transfer_failed': '❌',
  // What the server made of it.
  'webhook.received': '📥',
  'webhook.dropped': '❌',
  // The journey's happy ending: the invited row is finally usable.
  'webhook.linked': '🔗',
  'webhook.created_account': '⚠️',
  'webhook.user_deleted': '🗑️',
  'login.rejected': '🚫',
};

/** Keeps one step to one readable line even if a Clerk message runs long. */
export const MAX_TRACE_LINE = 300;

export const traceIdForEvent = (event: InviteTraceEvent): string =>
  event.traceId ?? invitationTraceId(event.email);

/**
 * `` `[inv-a3f2b1]` 🔴 webhook.dropped · no_email · type=user.created ``
 *
 * The leading backticked id is load-bearing: incoming webhooks cannot post into
 * a thread, so grouping is visual — the id renders monospaced (so lines align)
 * and a Slack search on that token returns the whole journey in order.
 *
 * Absent and `undefined` fields are dropped entirely rather than rendered as
 * `key=undefined`, so a step carries only what it actually knows.
 */
export const formatInviteTraceLine = (event: InviteTraceEvent): string => {
  const parts: string[] = [];
  if (event.email) parts.push(event.email);
  for (const [key, value] of Object.entries(event.fields ?? {})) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim().length === 0) continue;
    // Enum-ish values read better bare; anything else is labelled.
    parts.push(value === true ? key : `${key}=${String(value)}`);
  }
  const line = `\`[${traceIdForEvent(event)}]\` ${STEP_ICONS[event.step]} ${event.step}${
    parts.length > 0 ? ` · ${parts.join(' · ')}` : ''
  }`;
  return line.length <= MAX_TRACE_LINE
    ? line
    : `${line.slice(0, MAX_TRACE_LINE - 1)}…`;
};
